import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '../context/WebSocket/WebSocketContext';
import { WEB_SOCKET_CHANNELS } from '../utils';
import {
  addBulkOperation,
  removeBulkOperation,
  setBulkOperationError,
} from '../store/bulk/operations/slice';
import { addNotification } from '../store/notification/slice';
import { supabase } from '../services/supabaseService';
import { REACT_APP_FN_TABLE } from '../utils/envVar';
import {
  clearThreatLoadingInListCache,
  removeThreatFromListCache,
} from '../queries/threats';
import {
  ThreatStatusUpdatePayload,
  ThreatTypes,
  WebSocketMessage,
} from '../helpers/types';

interface UseHomeThreatSocketArgs {
  /** The current threat-list items (from useThreatListQuery). Used to match
   *  an errored WebSocket event back to a visible row so a bulk-operation
   *  error card can be shown for it. */
  threatItems: ThreatTypes[];
  /** Home's local networkData setter, fed by the asset_network_data channel. */
  setNetworkData: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * Headless hook owning the Home dashboard's two WebSocket subscriptions
 * (WHEEL-341d - moved out of Home.tsx inline effects, modelled on
 * useAiSummaryFlusher):
 *
 * - THREAT_STATUS_UPDATE: reconciles bulk operations + the threat list.
 *   On error it flags/creates the matching bulk operation, clears the row's
 *   optimistic `isLoading`, and pushes an error notification. On success it
 *   removes the resolved bulk operation, patches the FN candidate row (for
 *   `created` events), removes the threat from every cached list query, and
 *   pushes a success notification.
 * - ASSET_NETWORK_DATA: streams SSL/whois enrichment into Home's local
 *   `networkData` state.
 *
 * `threatItems` and `pendingOperations` are read through refs so the
 * subscription stays mounted once (subscribe/unsubscribe only when the
 * context callbacks change) instead of churning on every list/ops update -
 * preserving the exact handler behaviour the old inline effects had.
 */
export function useHomeThreatSocket({
  threatItems,
  setNetworkData,
}: UseHomeThreatSocketArgs): void {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  const pendingOperations = useSelector(
    (state: any) => state.bulkOperations.pendingOperations,
  );

  const threatItemsRef = useRef<ThreatTypes[]>(threatItems);
  threatItemsRef.current = threatItems;
  const pendingOperationsRef = useRef(pendingOperations);
  pendingOperationsRef.current = pendingOperations;

  useEffect(() => {
    const handleThreatStatusUpdate = (
      wsResponse: WebSocketMessage<ThreatStatusUpdatePayload>,
    ) => {
      const errorStatus = !!wsResponse?.data?.error;

      if (errorStatus) {
        const matchingOperations = Object.entries(
          pendingOperationsRef.current,
        ).filter(
          ([_, operation]: [string, any]) =>
            operation?.threatName === wsResponse?.data?.threat_name,
        );

        if (matchingOperations.length > 0) {
          matchingOperations.forEach(([key]) => {
            dispatch(
              setBulkOperationError({
                key,
                errorMessage: wsResponse.data.error || 'Unknown error occurred',
              }),
            );
          });
        } else {
          const matchingThreat = threatItemsRef.current?.find(
            (threat: any) =>
              threat.threat_name === wsResponse?.data?.threat_name,
          );

          if (matchingThreat) {
            dispatch(
              addBulkOperation([
                {
                  threatId: matchingThreat.threat_id,
                  threatName: matchingThreat.threat_name,
                  startedAt: new Date().toISOString(),
                  hasError: true,
                  errorMessage: wsResponse.data.error,
                  companyId: matchingThreat.company_id,
                  domainId: matchingThreat.protected_asset_id,
                  originalData: matchingThreat,
                  expectedStatus: matchingThreat.identification_status.value,
                },
              ]),
            );
          }
        }

        // Clear the optimistic isLoading marker for the errored threat
        // (previously CHANGE_BULK_STATUS_FAILURE in the threats reducer).
        clearThreatLoadingInListCache(
          queryClient,
          wsResponse?.data?.threat_name,
        );

        dispatch(
          addNotification({
            thread_name: wsResponse?.data?.threat_name || 'Unknown threat',
            status: wsResponse?.data?.status || 'error',
            userEmail:
              wsResponse?.data?.user_email || wsResponse?.data?.user_id || '-',
            date: wsResponse?.data?.timestamp || new Date().toISOString(),
            errorMessage: wsResponse?.data?.error || 'Unknown error occurred',
            isError: true,
            threat_id: wsResponse?.data?.threat_id ?? null,
            company_id: wsResponse?.data?.company_id ?? null,
            protected_asset_id: wsResponse?.data?.protected_asset_id ?? null,
            protected_asset_name:
              wsResponse?.data?.protected_asset_name ?? null,
            user_id: wsResponse?.data?.user_id ?? null,
          }),
        );
      } else {
        // Success case
        dispatch(
          addNotification({
            thread_name: wsResponse?.data?.threat_name,
            status: wsResponse?.data?.status || '-',
            userEmail:
              wsResponse?.data?.user_email || wsResponse?.data?.user_id || '-',
            date: wsResponse?.data?.timestamp || '-',
            errorMessage: null,
            isError: false,
            threat_id: wsResponse?.data?.threat_id ?? null,
            company_id: wsResponse?.data?.company_id ?? null,
            protected_asset_id: wsResponse?.data?.protected_asset_id ?? null,
            protected_asset_name:
              wsResponse?.data?.protected_asset_name ?? null,
            user_id: wsResponse?.data?.user_id ?? null,
            operation: wsResponse?.data?.operation ?? null,
          }),
        );

        // Clear the pending operation (which un-suppresses the row) using
        // every identifier the payload carries, not just threat_id - it is
        // optional on ThreatStatusUpdatePayload and a missing one used to
        // leave the operation pending forever. WHEEL-359.
        dispatch(
          removeBulkOperation({
            threatId: wsResponse?.data?.threat_id,
            companyId: wsResponse?.data?.company_id,
            domainId: wsResponse?.data?.protected_asset_id,
            threatName: wsResponse?.data?.threat_name,
            // Client clock, not the server timestamp: this dates the grace
            // period against our own in-flight requests.
            resolvedAt: new Date().toISOString(),
          }),
        );

        if (wsResponse.data.threat_id) {
          if (wsResponse?.data?.operation === 'created') {
            supabase
              .from(REACT_APP_FN_TABLE)
              .update({ created_threat_id: wsResponse.data.threat_id })
              .eq('data_source', 'manual')
              .eq(
                'created_threat_protected_asset_id',
                wsResponse.data.protected_asset_id,
              )
              .like('fn_url->>full_url', `%${wsResponse.data.threat_name}%`)
              .is('created_threat_id', null)
              .then(({ error }: { error: unknown }) => {
                if (error) {
                  console.error(
                    'Failed to update FN candidate with threat_id:',
                    error,
                  );
                }
              });
          }
        }

        // Remove the resolved threat from every cached list query
        // (previously CHANGE_STATUSLABEL_SUCCESS in the threats reducer).
        removeThreatFromListCache(queryClient, wsResponse?.data);
      }
    };

    const unsubscribe = subscribe(
      WEB_SOCKET_CHANNELS.THREAT_STATUS_UPDATE,
      handleThreatStatusUpdate,
    );
    return unsubscribe;
  }, [dispatch, subscribe, queryClient]);

  useEffect(() => {
    const handleAssetNetworkData = (wsResponse: any) => {
      setNetworkData((prevState: any) => ({
        ...prevState,
        [wsResponse?.data?.asset_id]: {
          ...prevState[wsResponse?.data?.asset_id],
          loader: false,
          ssl: wsResponse?.data?.data?.ssl_certificate ?? null,
          whois: wsResponse?.data?.data?.domain_information ?? null,
          whois_loading: wsResponse?.data?.whois_loading || false,
          ssl_loading: wsResponse?.data?.ssl_loading || false,
          ...(wsResponse?.data?.error && {
            ssl: null,
            whois: null,
            whois_loading: false,
            ssl_loading: false,
          }),
        },
      }));
    };

    const unsubscribe = subscribe(
      WEB_SOCKET_CHANNELS.ASSET_NETWORK_DATA,
      handleAssetNetworkData,
    );
    return unsubscribe;
  }, [subscribe, setNetworkData]);
}
